'use client';

import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

const VISITOR_ID_STORAGE_KEY = 'cineamo_ad_visitor_id';

type UseVisitorUidReturn = {
    // Stable per-visitor id (first-party, persisted in localStorage) — `undefined` while
    // resolving, `''` if persistence failed. Sent with the display request so the backend assigns
    // the same A/B variant to this visitor on every request.
    visitorId: string | undefined;
    // A fresh random id per call — used to dedupe impression/click POSTs on SQS retry (the backend
    // enforces a unique constraint on `uid`).
    generateUid: () => string;
};

/**
 * Resolves the stable, client-only first-party visitor id and exposes a generator for fresh
 * per-event ids. localStorage is only ever touched inside this query function — never during
 * render or on the server — so this hook is safe to call from server-rendered trees.
 */
export function useVisitorUid(): UseVisitorUidReturn {
    const { data: visitorId } = useQuery({
        queryKey: ['advertisement-visitor-id'],
        queryFn: async () => {
            try {
                const stored = localStorage.getItem(VISITOR_ID_STORAGE_KEY);
                if (stored) {
                    return stored;
                }
                // randomUUID is secure-context-only — fall back rather than throw into the catch,
                // so http contexts still get a persisted (sticky) id.
                const generated =
                    typeof crypto.randomUUID === 'function'
                        ? crypto.randomUUID()
                        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
                localStorage.setItem(VISITOR_ID_STORAGE_KEY, generated);
                return generated;
            } catch {
                // Without a persisted id (e.g. private mode) the backend falls back to random
                // variant selection.
                return '';
            }
        },
        staleTime: Infinity
    });

    const generateUid = useCallback(() => {
        // The backend uid columns are BIGINT, so the id must be a positive integer decimal string
        // — a UUID string is rejected at ingestion. 53 random bits (Number.MAX_SAFE_INTEGER, fits
        // BIGINT) keep same-millisecond collisions a non-issue while staying exactly representable,
        // so no BigInt is needed (its literals require an ES2020+ build target). The previous
        // snowflake setup collapsed its machine-id to NaN and collided.
        if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
            const buf = new Uint32Array(2);
            crypto.getRandomValues(buf);
            // buf[0] (32 bits) << 21 + top 21 bits of buf[1] → an integer in [0, 2^53 - 1].
            return (buf[0] * 0x200000 + (buf[1] >>> 11)).toString();
        }
        return `${Date.now()}${Math.floor(Math.random() * 1_000_000)}`;
    }, []);

    return { visitorId, generateUid };
}
